import React, { useEffect, useState } from 'react';
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { BarChart } from '@mui/x-charts/BarChart';
import { addDays, endOfDay, format, startOfDay } from 'date-fns';
import { getWeekRangeInLocalTime } from '../../../CANpark/utils/getWeekRangeInLocalTime';
import { getTicketStatsForParkingLocation } from 'api/canpark/tickets/getTicketStatsForParkingLocation';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { useTicketTypeMeta } from 'containers/pages/CANpark/context/TicketTerminologyProvider';

interface DayPoint {
	day: string;
	logs: number;
	tags: number;
	tows: number;
}

const CHART_HEIGHT = 300;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const CustomStackedBarChart: React.FC<{ parkingLocation: ParkingLocation }> = ({
	parkingLocation,
}) => {
	const getTicketTypeMeta = useTicketTypeMeta();
	const [weekOffset, setWeekOffset] = useState(0);
	const [data, setData] = useState<DayPoint[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			try {
				setLoading(true);
				setError(null);
				const { startDate } = getWeekRangeInLocalTime(weekOffset);
				const promises = Array.from({ length: 7 }, (_, i) =>
					getTicketStatsForParkingLocation(
						parkingLocation.id,
						startOfDay(addDays(startDate, i)),
						endOfDay(addDays(startDate, i)),
					),
				);
				const stats = await Promise.all(promises);
				if (cancelled) return;
				setData(
					stats.map((s, i) => ({
						day: format(addDays(startDate, i), "EEE',' do MMM"),
						logs: parseInt(s.logTickets),
						tags: parseInt(s.logAndWarnTickets),
						tows: parseInt(s.towTickets),
					})),
				);
			} catch {
				if (!cancelled) setError('Failed to load data');
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		fetchData();
		return () => {
			cancelled = true;
		};
	}, [parkingLocation.id, weekOffset]);

	const { startDate, endDate } = getWeekRangeInLocalTime(weekOffset);
	const formattedWeekRange = `${format(startDate, 'd MMM')} – ${format(
		endDate,
		'd MMM yyyy',
	)}`;
	const isEmpty =
		data.length > 0 &&
		data.every((d) => d.logs === 0 && d.tags === 0 && d.tows === 0);

	return (
		<Card sx={{ width: '100%', maxWidth: 450 }}>
			<CardHeader title="Daily Violations" sx={{ pb: 0 }} />
			<CardContent sx={{ pt: 1 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						mb: 1,
						gap: 1,
					}}
				>
					<Tooltip title="Previous week">
						<IconButton
							size="small"
							onClick={() => setWeekOffset(weekOffset + 1)}
						>
							<ChevronLeft />
						</IconButton>
					</Tooltip>
					<Typography variant="caption" color="text.secondary">
						{formattedWeekRange}
					</Typography>
					<Tooltip
						title={
							weekOffset === 0
								? 'Already at the latest week'
								: 'Next week'
						}
					>
						<span>
							<IconButton
								size="small"
								onClick={() => setWeekOffset(weekOffset - 1)}
								disabled={weekOffset === 0}
							>
								<ChevronRight />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
				{loading ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: CHART_HEIGHT,
						}}
					>
						<CircularProgress />
					</Box>
				) : error ? (
					<Typography
						color="error"
						sx={{ textAlign: 'center', py: 4 }}
					>
						{error}
					</Typography>
				) : isEmpty ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: CHART_HEIGHT,
						}}
					>
						<Typography variant="body2" color="text.secondary">
							No violations this week
						</Typography>
					</Box>
				) : (
					<BarChart
						height={CHART_HEIGHT}
						xAxis={[
							{
								scaleType: 'band',
								data: data.map((d) => d.day),
							},
						]}
						yAxis={[{ tickMinStep: 1 }]}
						series={[
							{
								data: data.map((d) => d.logs),
								label: cap(getTicketTypeMeta('log').pluralLabel),
								stack: 'total',
								color: getTicketTypeMeta('log').hexColor,
							},
							{
								data: data.map((d) => d.tags),
								label: cap(
									getTicketTypeMeta('logAndWarn').pluralLabel,
								),
								stack: 'total',
								color: getTicketTypeMeta('logAndWarn').hexColor,
							},
							{
								data: data.map((d) => d.tows),
								label: cap(getTicketTypeMeta('tow').pluralLabel),
								stack: 'total',
								color: getTicketTypeMeta('tow').hexColor,
							},
						]}
						margin={{ top: 16, right: 16, left: 0, bottom: 24 }}
					/>
				)}
			</CardContent>
		</Card>
	);
};

export default CustomStackedBarChart;
